import { useMemo, useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Chart from "@/v2-ui/chart"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { CUSTOMER_VIEW_TAB_PARAM } from "@/v2-backoffice-client/customer/customer.links"
import type { ChurnRiskLevel } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  HEALTH_BREAK_COLORS,
  HEALTH_BREAK_LABELS,
  HEALTH_BREAKS,
  type PortfolioHealthBreak,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import {
  getHealthBreak,
  type PortfolioCustomerSummary,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioMapProps = ComponentProps<"section"> & {
  customers: PortfolioCustomerSummary[]
}

type PortfolioMapPoint = {
  externalCustomerId: string
  name: string
  /** Engagement score of the active seats (x). */
  score: number
  /** Share of seats active in the last 28 days, in % (y). */
  activeShare: number
  /** Seat count (bubble size). */
  seats: number
  activeSeats: number
  level: ChurnRiskLevel
  trendPct: number | null
  break: PortfolioHealthBreak
  fill: string
}

const CHART_CONFIG = {}

// Every scored customer on one canvas: how engaged the active users are (x)
// versus how much of the license is actually in use (y), bubble sized by
// seats and colored by churn risk. The bottom-right corner IS the
// engagement-vs-churn paradox — strong users, idle licenses — and those are
// the accounts to activate before renewal. Click a bubble to open the
// customer's Activity tab; click a legend level to hide/show its bubbles.
function PortfolioMap(props: PortfolioMapProps) {
  const { customers, className, ...sectionProps } = props
  const opener = useAppViewSheetOpener()

  const [hiddenBreaks, setHiddenBreaks] = useState<Set<PortfolioHealthBreak>>(
    () => new Set(),
  )

  function toggleBreak(healthBreak: PortfolioHealthBreak) {
    setHiddenBreaks((prev) => {
      const next = new Set(prev)
      if (next.has(healthBreak)) next.delete(healthBreak)
      else next.add(healthBreak)
      return next
    })
  }

  const points: PortfolioMapPoint[] = useMemo(
    () =>
      customers
        .filter(
          (customer) =>
            customer.summary.avgScore != null && customer.summary.seatCount > 0,
        )
        .map((customer) => {
          const { summary } = customer
          const healthBreak = getHealthBreak(customer)
          return {
            externalCustomerId: customer.externalCustomerId,
            name: customer.name,
            score: summary.avgScore ?? 0,
            activeShare: Math.round(
              (summary.activeUsers28d / summary.seatCount) * 100,
            ),
            seats: summary.seatCount,
            activeSeats: summary.activeUsers28d,
            level: summary.churnRisk.level,
            trendPct: summary.trendPct,
            break: healthBreak,
            fill: HEALTH_BREAK_COLORS[healthBreak],
          }
        }),
    [customers],
  )

  const countsByBreak = useMemo(() => {
    const counts = new Map<PortfolioHealthBreak, number>()
    for (const point of points) {
      counts.set(point.break, (counts.get(point.break) ?? 0) + 1)
    }
    return counts
  }, [points])

  const visiblePoints = points.filter((point) => !hiddenBreaks.has(point.break))

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Map"
        title="Portfolio map"
        lead="Every scored customer placed by engagement and by how much of its license is in use; bubble size is seats. Top-left needs a call, bottom-right has keen users on an idle license."
        info="Engagement of the active seats (→) vs share of licenses in use (↑), bubble = seat count. 'At risk & falling' is at high churn risk AND still losing usage — call those first. Bottom-right is the paradox corner: enthusiastic users on a mostly idle license — activate the quiet seats before the renewal. Click a bubble to open the account; click a legend level to hide or show its bubbles."
      >
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          {HEALTH_BREAKS.map((healthBreak) => {
            const isHidden = hiddenBreaks.has(healthBreak)
            return (
              <button
                key={healthBreak}
                type="button"
                aria-pressed={!isHidden}
                title={
                  isHidden
                    ? "Hidden — click to show these bubbles"
                    : "Shown — click to hide these bubbles"
                }
                className={cn(
                  "flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
                  isHidden
                    ? "border-transparent text-muted-foreground/60 hover:bg-muted/30"
                    : "border-border bg-muted/40 hover:bg-muted/60",
                )}
                onClick={() => toggleBreak(healthBreak)}
              >
                <span
                  className={cn(
                    "size-2 rounded-full",
                    isHidden && "opacity-30",
                  )}
                  style={{
                    backgroundColor: HEALTH_BREAK_COLORS[healthBreak],
                  }}
                />
                <span className={cn(isHidden && "line-through")}>
                  {HEALTH_BREAK_LABELS[healthBreak]}
                </span>
                <span className="tabular-nums opacity-70">
                  {formatNumber(countsByBreak.get(healthBreak) ?? 0)}
                </span>
              </button>
            )
          })}
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1 min-h-0">
        {points.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No scored customers yet — the map fills in as engagement scores
            accumulate.
          </p>
        ) : visiblePoints.length === 0 ? (
          <p className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
            Every level is hidden — click a level in the legend to bring its
            bubbles back.
          </p>
        ) : (
          <Chart config={CHART_CONFIG} className="h-[280px] w-full flex-1">
            <Chart.ScatterChart
              margin={{ left: -8, right: 8, top: 8, bottom: 14 }}
            >
              <Chart.Grid strokeDasharray="3 3" />
              <Chart.Axis.X
                type="number"
                dataKey="score"
                name="Engagement"
                domain={[0, 100]}
                tickLine={false}
                axisLine={false}
                className="text-xs text-muted-foreground"
              >
                <Chart.Label
                  value="Engagement score of the active seats (0–100) →"
                  position="insideBottom"
                  offset={-12}
                  fontSize={11}
                  className="fill-muted-foreground"
                />
              </Chart.Axis.X>
              <Chart.Axis.Y
                type="number"
                dataKey="activeShare"
                name="Seats active"
                domain={[0, 100]}
                tickFormatter={(value: number) => `${value}%`}
                tickLine={false}
                axisLine={false}
                width={58}
                className="text-xs text-muted-foreground"
              >
                <Chart.Label
                  value="Share of licenses in use ↑"
                  angle={-90}
                  position="insideLeft"
                  offset={4}
                  style={{ textAnchor: "middle" }}
                  fontSize={11}
                  className="fill-muted-foreground"
                />
              </Chart.Axis.Y>
              <Chart.Axis.Z
                type="number"
                dataKey="seats"
                // Generous area range: big accounts must read as BIG at a
                // glance — they're where the effort pays.
                range={[60, 1100]}
                name="Seats"
              />
              {/* cursor/animations off: the crosshair chasing the mouse and
                the tooltip easing read as flicker on a dense scatter. */}
              <Chart.Tooltip
                cursor={false}
                isAnimationActive={false}
                content={<PortfolioMapTooltip />}
              />
              <Chart.Scatter
                data={visiblePoints}
                isAnimationActive={false}
                activeShape={false}
                className="cursor-pointer"
                onClick={(point: { payload?: PortfolioMapPoint }) => {
                  const target = point.payload
                  if (!target) return
                  opener.open("customer", target.externalCustomerId, {
                    [CUSTOMER_VIEW_TAB_PARAM]: "sign-ins",
                  })
                }}
              >
                {visiblePoints.map((point) => (
                  <Chart.BarCell
                    key={point.externalCustomerId}
                    fill={point.fill}
                    fillOpacity={0.8}
                  />
                ))}
              </Chart.Scatter>
            </Chart.ScatterChart>
          </Chart>
        )}
      </UsageReportCard>
    </section>
  )
}

/**
 * Name + badges, then one labeled row per axis so the numbers can't be
 * mixed up: which is the score, which is license use, what sizes the
 * bubble.
 */
function PortfolioMapTooltip(props: {
  active?: boolean
  payload?: { payload: PortfolioMapPoint }[]
}) {
  const { active, payload } = props
  const point = payload?.[0]?.payload
  if (!active || !point) return null

  return (
    <div className="flex flex-col gap-1.5 rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="flex items-center gap-2 font-medium">
        {point.name}
        <UsageChurnRiskBadge level={point.level} />
      </p>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
        <span className="text-muted-foreground">Engagement score →</span>
        <span className="text-right font-medium tabular-nums">
          {point.score} / 100
        </span>
        <span className="text-muted-foreground">Licenses in use ↑</span>
        <span className="text-right font-medium tabular-nums">
          {point.activeShare}%{" "}
          <span className="font-normal text-muted-foreground">
            ({formatNumber(point.activeSeats)} of {formatNumber(point.seats)}{" "}
            seats)
          </span>
        </span>
        <span className="text-muted-foreground">Activity trend · 28 d</span>
        <span className="text-right">
          <UsageTrendBadge trendPct={point.trendPct} />
        </span>
      </div>
    </div>
  )
}

export default PortfolioMap
